import { Button, Chip, Stack, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid-premium";
import { cacheService, setupService } from "../../../../Core/Api/services";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { DbMetadataFieldInfos } from "../../../../Core/Api/Types/db-metadata-field-infos";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import { ModalContext } from "../../../../Components/Modals";
import ModalCreateMetadata, {
  ModalCreateMetadataElementProps,
} from "../../../../Components/Modals/ModalCreateMetadata/modal-create-metadata";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import ModalConfirmAction, {
  ModalConfirmActionProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import TableCell2Lines from "../../../../Components/Table/TableCell2Lines/table-cell-2-lines";
import TableCellChip from "../../../../Components/Table/TableCellChip/table-cell-chip";
import AddIcon from "@mui/icons-material/Add";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";

function AdminSettingsMetadataFields() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [metadataFields, setMetadatafields] = React.useState<DbMetadataFieldInfos[]>([]);
  const enqueueCustomSnackbar = useCustomSnackbar();

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const apiGrid = useGridApiRef();

  const handleOpenModalNewMetadata = React.useCallback(
    (type: string, metadata?: DbMetadataFieldInfos) => {
      if (!openModal) return;
      const onClose: ModalCreateMetadataElementProps["onClose"] = (data) => {};

      openModal<ModalCreateMetadataElementProps>(ModalCreateMetadata, {
        onClose,
        type: type,
        metadata: metadata,
        onConfirm: onConfirm,
      });
    },
    [openModal]
  );

  const loadMetadataFields = () => {
    setIsLoading(true);
    setupService.GetAllMetadataFields().then(function (response) {
      if (response.success) {
        if (response.metadataFields) {
          cacheService.SetMetadataFields(response.metadataFields);
          cacheService.Resync();
          setMetadatafields(response.metadataFields);
        }
        setIsLoading(false);
      }
    });
  };

  const onClickCopy = (metadata: DbMetadataFieldInfos) => {
    handleOpenModalNewMetadata("Copy", metadata);
  };

  const onClickModify = (metadata: DbMetadataFieldInfos) => {
    handleOpenModalNewMetadata("Modify", metadata);
  };

  const onClickCreate = () => {
    handleOpenModalNewMetadata("Create");
  };

  const onConfirm = (metadata: DbMetadataFieldInfos, type: string) => {
    if (type === "Create" || type === "Copy") {
      setupService.CreateMetadataField(metadata).then(function (response) {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.newMetadataCreated"), "success");
          loadMetadataFields();
        } else enqueueCustomSnackbar(t("alert:error.metadataCreationFailed", { message: response.message }), "error");
      });
    }
    if (type === "Modify") {
      setupService.UpdateMetadataField(metadata).then(function (response) {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.metadataModified"), "success");
          loadMetadataFields();
        } else enqueueCustomSnackbar(t("alert:error.metadataModificationFailed", { message: response.message }), "error");
      });
    }
  };

  const onClickDeleteTruely = (id: number) => {
    //ModalConfirmActionProps
    setupService.DeleteMetadataField(id).then(function (response) {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.metadataDeleted"), "success");
        loadMetadataFields();
      }
    });
  };

  const onClickDelete = React.useCallback(
    (id: number) => {
      if (!openModal) return;

      openModal<ModalConfirmActionProps>(ModalConfirmAction, {
        title: t("modal:title.deleteMetadataField"),
        commentLabel: t("modal:comment.confirmDeleteMetadataField"),
        closeModal: () => {},
        onConfirm: () => {
          onClickDeleteTruely(id);
        },
      });
    },
    [openModal]
  );

  const metadataRows: GridRowsProp = metadataFields;
  type Row = (typeof metadataRows)[number];

  const metadataColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "Id",
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: Row) => (
        <Typography variant="body2" p={0.5}>
          {data.value}
        </Typography>
      ),
    },
    {
      field: "tagName",
      headerName: t("table:headerName.labelAndTagName"),
      flex: CellSize.large,
      minWidth: CellSize.large,
      valueGetter: (params: any, row: any) => row?.label + " - " + row?.tagName,
      renderCell: (data: Row) => <TableCell2Lines title={data?.row?.label} desc={data?.row?.tagName} />,
    },
    {
      field: "objectType",
      headerName: t("table:headerName.objectType"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: Row) => <Typography variant="body2">{data.value}</Typography>,
    },
    {
      field: "valuesCardinality",
      headerName: t("field:label.cardinality"),
      flex: CellSize.small,
      minWidth: CellSize.medium,
      renderCell: (data: Row) => <Typography variant="body2">{data.value}</Typography>,
    },
    {
      field: "isSearchable",
      headerName: t("table:headerName.searchable"),
      flex: CellSize.small,
      minWidth: CellSize.medium,
      type: "boolean",
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "isEditable",
      headerName: t("table:headerName.editable"),
      flex: CellSize.small,
      minWidth: CellSize.medium,
      type: "boolean",
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "isMandatory",
      headerName: t("common:mandatory"),
      flex: CellSize.small,
      minWidth: CellSize.medium,
      type: "boolean",
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "isInternal",
      headerName: t("table:headerName.internal"),
      flex: CellSize.small,
      minWidth: CellSize.medium,
      type: "boolean",
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "isShared",
      headerName: t("common:shared"),
      flex: CellSize.small,
      minWidth: CellSize.medium,
      type: "boolean",
      valueGetter: (params: any, row: any) => row?.platformId === null,
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "isUsedOnBundleMaker",
      headerName: t("table:headerName.usedOnBundleMaker"),
      flex: CellSize.small,
      minWidth: CellSize.medium,
      type: "boolean",
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "allowPopulate",
      headerName: t("table:headerName.populate"),
      flex: CellSize.small,
      minWidth: CellSize.medium,
      type: "boolean",
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      field: "layer",
      headerName: t("common:layer"),
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: Row) => <TableCellChip data={data} />,
    },
    {
      field: "objectStatuses",
      headerName: t("table:headerName.assetStatuses"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: Row) =>
        data.value ? (
          <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
            {data.value.map((x: string) => (
              <Chip label={x} key={x} size="small" />
            ))}
          </Stack>
        ) : (
          ""
        ),
    },
    {
      field: "objectTypes",
      headerName: t("table:headerName.assetTypes"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: Row) =>
        data.value ? (
          <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
            {data.value.map((x: string) => (
              <Chip label={x} key={x} color="primary" size="small" />
            ))}
          </Stack>
        ) : (
          ""
        ),
    },
    {
      field: "titleType",
      headerName: t("field:label.titleTypeLowerCase"),
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: Row) => <Typography variant="body2">{data.value}</Typography>,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<ContentCopyOutlinedIcon sx={{ width: 20, height: 20 }} />}
          label={t("common:copy")}
          onClick={() => onClickCopy(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<EditOutlinedIcon sx={{ width: 20, height: 20 }} />}
          label={t("common:modify")}
          onClick={() => onClickModify(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<DeleteOutlinedIcon sx={{ width: 20, height: 20 }} />}
          label={t("common:delete")}
          onClick={() => onClickDelete(params.row["id"])}
          showInMenu
        />,
      ],
    },
  ];

  useEffect(() => {
    loadMetadataFields();
  }, []);

  const additionalActions = (
    <Button onClick={onClickCreate} variant="contained" color="primary" startIcon={<AddIcon />}>
      {t("button:createMetadata")}
    </Button>
  );

  return (
    <DataTable
      loading={isLoading}
      tableKey={TableUuids.SETTINGS_METADATA_FIELDS}
      rows={metadataRows}
      columns={metadataColumns}
      additionalActions={additionalActions}
      apiRef={apiGrid}
      onRowClick={(params) => onClickModify(params.row)}
    />
  );
}

export default AdminSettingsMetadataFields;
